import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { PipelineExpandedRuns } from '@/features/pipelines/components/PipelineTableV2NestedRuns';
import { useDeletePipeline, useUpdatePipeline } from '@/features/pipelines/hooks/pipelines';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { OperationTableV2DashCell } from '@/features/shared/components/tableSystem/OperationTableV2Cells';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildEntityMetricColgroup } from '@/features/shared/components/tableSystem/TableV2MetricColumnLayout';
import { TableV2MetricCells } from '@/features/shared/components/tableSystem/TableV2MetricColumns';
import { tableV2NestedCarrierRowSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2ProjectCell,
  TableV2SortableHeader,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { buildTagColorByName } from '@/features/shared/tags';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { resolveVisibleMetricThresholdKeys } from '@/shared/utils/metricsThresholds';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { MetricThresholdGroup, PipelineListItemDto, QueryResult } from '@flowqa/shared';

export type PipelineTableV2SortKey =
  | 'latestRunTimestamp'
  | 'metrics.averageDurationMs'
  | 'metrics.flakeScore'
  | 'metrics.frequencyPerDay'
  | 'metrics.passRate'
  | 'name';

const metricSortByKey: Partial<Record<PipelineTableV2SortKey, string>> = {
  'metrics.averageDurationMs': 'executionAverageDurationMs',
  'metrics.flakeScore': 'executionFlakeScore',
  'metrics.frequencyPerDay': 'executionFrequencyPerDay',
  'metrics.passRate': 'executionPassRate',
};

const baseSkeletonColumns = ['chevron', 'name', 'status', 'dateTime', 'tags', 'actions'] as const;

const buildSkeletonColumns = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject: boolean,
): TableV2SkeletonColumnKind[] => [
  ...baseSkeletonColumns.slice(0, 4),
  ...visibleMetricKeys.map(() => 'metric' as const),
  ...baseSkeletonColumns.slice(4, 5),
  ...(includeProject ? (['project'] satisfies TableV2SkeletonColumnKind[]) : []),
  ...baseSkeletonColumns.slice(5),
];

const buildColgroup = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject: boolean,
): ReactElement => buildEntityMetricColgroup(visibleMetricKeys, includeProject);

const toApiSort = (sort: ApiSort): ApiSort => ({
  field: metricSortByKey[sort.field as PipelineTableV2SortKey] ?? sort.field,
  direction: sort.direction,
});

const resolvePipelineRunChildCount = (row: PipelineListItemDto): number =>
  row.buildTotal ?? row.runSummaries?.length ?? 0;

type PipelinesTableV2Props = {
  active?: boolean;
  initialPageSize?: number;
  metricsEndDay?: string | null;
  metricsStartDay?: string | null;
  noRowsText: string;
  onQueryMeta?: (meta: { total: number; items: PipelineListItemDto[] } | null) => void;
  pageSizeOptions?: number[];
  queryFn: (input: GridQueryInput) => Promise<QueryResult<PipelineListItemDto>>;
  queryKey: QueryKey;
  search?: string;
  showProjectColumn?: boolean;
  sort: ApiSort;
  tagOptionsOverride?: TagOption[];
  onSortChange: (sort: ApiSort) => void;
};

const PipelineRow = ({
  deletePipelinePending,
  editPending,
  isLastVisibleRow,
  organisationId,
  row,
  tagColorByName,
  thresholds,
  visibleMetricKeys,
  metricsEndDay,
  metricsStartDay,
  showProjectColumn,
  onDeleteRow,
  onEditRow,
}: {
  deletePipelinePending: boolean;
  editPending: boolean;
  isLastVisibleRow: boolean;
  organisationId: string | null;
  row: PipelineListItemDto;
  tagColorByName: Partial<Record<string, TagColor>>;
  thresholds: MetricThresholdGroup;
  visibleMetricKeys: MetricThresholdGroupKey[];
  metricsEndDay: string | null;
  metricsStartDay: string | null;
  showProjectColumn: boolean;
  onDeleteRow: (row: PipelineListItemDto) => void;
  onEditRow: (row: PipelineListItemDto) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const href = `/pipelines/${resolvePipelineSlug({ id: row.id, name: row.name })}`;
  const childCount = resolvePipelineRunChildCount(row);

  return (
    <>
      <TableRow
        sx={buildTableV2BodyRowSurfaceSx({
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse pipeline runs' : 'Expand pipeline runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={href}
            primary={row.name}
            secondary={row.project?.name ?? row.defaultBranch ?? null}
          />
        </TableCell>
        <TableCell>
          {row.latestRun ? (
            <TableV2StatusChip status={row.latestRun.status} />
          ) : (
            <OperationTableV2DashCell />
          )}
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.latestRun?.timestamp ?? null} />
        </TableCell>
        <TableV2MetricCells
          metrics={row.metrics ?? null}
          thresholds={thresholds}
          visibleMetricKeys={visibleMetricKeys}
        />
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        {showProjectColumn ? (
          <TableCell>
            <TableV2ProjectCell project={null} fallbackName={row.project?.name} />
          </TableCell>
        ) : null}
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={row.name}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Pipeline')}
            deleteActionLabel={getEntityActionLabel('delete', 'Pipeline')}
            rowTags={row.tags ?? []}
            onEdit={() => {
              onEditRow(row);
            }}
            onDelete={() => {
              onDeleteRow(row);
            }}
            disableEdit={editPending || deletePipelinePending}
            disableDelete={deletePipelinePending}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? { '& > td': { borderBottom: (theme) => `1px solid ${theme.app.colors.border}` } }
            : {},
        ]}
      >
        <TableCell colSpan={buildSkeletonColumns(visibleMetricKeys, showProjectColumn).length}>
          <PipelineExpandedRuns
            childCount={childCount}
            expanded={expanded}
            organisationId={organisationId}
            pipelineId={row.id}
            projectId={row.projectId}
            metricsEndDay={metricsEndDay}
            metricsStartDay={metricsStartDay}
            tagColorByName={tagColorByName}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const PipelinesTableV2 = ({
  active = true,
  initialPageSize = 10,
  metricsEndDay = null,
  metricsStartDay = null,
  noRowsText,
  onQueryMeta,
  onSortChange,
  pageSizeOptions = [10, 25, 50],
  queryFn,
  queryKey,
  search,
  showProjectColumn = false,
  sort,
  tagOptionsOverride = [],
}: PipelinesTableV2Props): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const updatePipeline = useUpdatePipeline();
  const deletePipeline = useDeletePipeline();
  const [editTarget, setEditTarget] = useState<PipelineListItemDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PipelineListItemDto | null>(null);
  const editEditor = useDetailEditEditor({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Pipeline updated',
    saveEntity: async ({ name, tags }) => {
      if (!editTarget) return;
      await updatePipeline.mutateAsync({ id: editTarget.id, name, tags });
    },
  });
  const visibleMetricKeys = useMemo(
    () => resolveVisibleMetricThresholdKeys(thresholds.pipelines),
    [thresholds.pipelines],
  );
  const tagColorByName = useMemo(
    () => buildTagColorByName(tagOptionsOverride),
    [tagOptionsOverride],
  );
  const handleSortChange = (field: PipelineTableV2SortKey): void => {
    onSortChange({
      field,
      direction: sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc',
    });
  };
  const header = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="name"
          label="Pipeline"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2HeaderLabel label="Latest Status" />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="latestRunTimestamp"
          label="Ran At"
          onSortChange={handleSortChange}
        />
      </TableCell>
      {visibleMetricKeys.includes('passRate') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="metrics.passRate"
            label="Success Rate"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {visibleMetricKeys.includes('frequency') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="metrics.frequencyPerDay"
            label="Frequency"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {visibleMetricKeys.includes('avgDuration') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="metrics.averageDurationMs"
            label="Avg. Duration"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {visibleMetricKeys.includes('flakeScore') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="metrics.flakeScore"
            label="Flake Rate"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      <TableCell>
        <TableV2HeaderLabel label="Tags" />
      </TableCell>
      {showProjectColumn ? (
        <TableCell>
          <TableV2HeaderLabel label="Project" />
        </TableCell>
      ) : null}
      <TableCell align="center" sx={{ p: s('none') }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Pipelines table"
        colgroup={buildColgroup(visibleMetricKeys, showProjectColumn)}
        emptyText={noRowsText}
        entityLabel="pipelines"
        entitySingularLabel="pipeline"
        header={header}
        initialPageSize={initialPageSize}
        onQueryMeta={onQueryMeta}
        pageSizeOptions={pageSizeOptions}
        queryFn={queryFn}
        queryKey={queryKey}
        renderRow={(row, { isLastVisibleRow }) => (
          <PipelineRow
            key={row.id}
            deletePipelinePending={deletePipeline.isPending}
            editPending={editEditor.isPending}
            isLastVisibleRow={isLastVisibleRow}
            metricsEndDay={metricsEndDay}
            metricsStartDay={metricsStartDay}
            organisationId={selectedOrganisationId}
            row={row}
            showProjectColumn={showProjectColumn}
            tagColorByName={tagColorByName}
            thresholds={thresholds.pipelines}
            visibleMetricKeys={visibleMetricKeys}
            onDeleteRow={setDeleteTarget}
            onEditRow={setEditTarget}
          />
        )}
        search={search}
        skeletonColumns={buildSkeletonColumns(visibleMetricKeys, showProjectColumn)}
        sorts={[toApiSort(sort)]}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Pipeline"
        entityName={editTarget?.name ?? ''}
        nameValue={editTarget?.name ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        loading={editEditor.isPending}
        cascadeCopy="Tags will be applied to all associated jobs."
        onClose={() => {
          if (editEditor.isPending) return;
          setEditTarget(null);
        }}
        onSave={async ({ name, tags, colors }) => {
          await editEditor.save({ name, tags, colors });
          setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'pipeline' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'pipeline',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'pipeline' })}
        confirmColor="red"
        loading={deletePipeline.isPending}
        onClose={() => {
          if (deletePipeline.isPending) return;
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) return;
          const deletedPipelineName = deleteTarget.name;
          deletePipeline.mutate(
            { id: deleteTarget.id, projectId: deleteTarget.projectId },
            {
              onSuccess: () => {
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'pipeline',
                    name: deletedPipelineName,
                  }),
                  tone: 'green',
                });
                setDeleteTarget(null);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};
